import React, { useMemo } from 'react';
import { Modal } from 'framework/ui/ui-builder';
import { CreateForm } from 'framework/form';
import { useShowContext } from 'framework/show';
import HealthcheckFormContent from './createUpdate/HealthcheckFormContent';

interface ValidationSchema {
  validate: (data: unknown, options?: { abortEarly?: boolean }) => Promise<unknown>;
}

interface FormFieldsData {
  [key: string]: unknown;
}

interface HealthcheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  data?: { date?: string; description?: string };
  submitFormFunc?: (data: FormFieldsData) => Promise<void | object>;
  getValidationSchemaFunc?: () => Promise<ValidationSchema>;
  afterSubmit?: { execute: (data?: unknown) => void };
}

const HealthcheckModal: React.FC<HealthcheckModalProps> = ({
  isOpen,
  onClose,
  data,
  submitFormFunc,
  getValidationSchemaFunc,
  afterSubmit,
}) => {
  const { data: contextData, setData } = useShowContext();

  const initialFormData = useMemo(() => {
    if (data?.date) {
      return { date: data.date, description: data.description || '' };
    }
    if (contextData?.retentionRiskLastHealthcheck) {
      return { date: contextData.retentionRiskLastHealthcheck, description: '' };
    }
    return { date: '', description: '' };
  }, [data, contextData]);

  const enhancedAfterSubmit = useMemo(() => {
    if (!afterSubmit?.execute) {
      return {
        execute: (response: any) => {
          if (response?.lastHealthcheckDate && setData && contextData) {
            setData({
              ...contextData,
              retentionRiskLastHealthcheck: response.lastHealthcheckDate,
            });
          }
          onClose();
        },
      };
    }

    return {
      execute: (response: any) => {
        if (response?.lastHealthcheckDate && setData && contextData) {
          setData({
            ...contextData,
            retentionRiskLastHealthcheck: response.lastHealthcheckDate,
          });
        }
        afterSubmit.execute(response);
      },
    };
  }, [afterSubmit, setData, contextData, onClose]);

  return (
    <CreateForm
      submitFormFunc={submitFormFunc || (() => Promise.resolve())}
      getValidationSchemaFunc={getValidationSchemaFunc}
      initialData={initialFormData}
      afterSubmit={enhancedAfterSubmit}
    >
      <Modal
        paperStyles={{
          background: 'var(--mui-palette-background-paper)',
        }}
        isOpen={isOpen}
        onToggle={onClose}
        hasCancelButton={false}
      >
        <HealthcheckFormContent onClose={onClose} />
      </Modal>
    </CreateForm>
  );
};

export default HealthcheckModal;
